/**
 * The controls under the coach's line for every opener offer (PC-I71,
 * PC-I75): the stale routine (#274), the lagging region (#273), a plateau's
 * variation, rep range and deload (#272), and the injured area's ask (#277).
 *
 * One component for all of them (#318, PC-I145), so no kind can drift from
 * the others. What each shows, and the words, are decided in
 * `offerPresentation.ts` (with `addOfferPresentation.ts` and
 * `adjustOfferPresentation.ts` behind it) from the shared deck, and they all
 * arrive as the same four states (`OfferView`); the line above is the
 * backend's, in code's words. The verdict's own controls (`ProposalControls`,
 * #270) stay in `VerdictOpener.tsx`: its answers, its quiet accept under rest
 * and its absent decline are its own, and it shares this component's look
 * through `QUIET` and the same recessed well.
 *
 * Under the bubble, where "Got it" sits for every other trigger: buttons
 * below the coach's words, never inside them (PC-I41).
 *
 * - **An offer**: an amber pill on the recessed well, which *wraps* and never
 *   truncates (a template is named by its user, and a catalog exercise's name
 *   is long), a quiet decline that records itself and folds the line away, so
 *   there is no "Got it" beside it (PC-I75), and one scope line.
 * - **An ask**: two answers of equal weight, two identical cream pills on the
 *   recessed well, since neither is the advice (PC-I68).
 * - **After a yes**: the record line, from the answer the server recorded
 *   (PC-I118), above "Undo" and "Got it" (PC-I57). Undo is drawn only where
 *   the server would take it (PC-I72, PC-I111) and sends the contract's
 *   "withdrawn".
 *
 * Nothing changes on screen unless the server took the answer (PC-I42); a
 * write that was not saved says so and leaves the controls where they were
 * (PC-I54); a refusal because the line is out of date reloads it instead
 * (PC-I61). The control the keyboard was on is replaced when the answer lands
 * (the offer by its record line and Undo, and back), so the shared run hook
 * keeps focus in the controls (PC-I54, PC-I61, #317).
 */
import type { OpenerOffer } from "../../api/types";
import { COACH_COPY } from "../../copy/coach";
import { RECESSED } from "../builder/surfaces";
import { TOKENS } from "../ui/dashboard-primitives";
import { OFFER_WITHDRAWN } from "./offers";
import { offerView, showsOfferGotIt } from "./offerPresentation";
import { useOpenerRun } from "./useOpenerRun";
import { OpenerWriteFailed, QUIET } from "./VerdictOpener";

/** The amber accept: the only amber on the line, the one thing to act on.
 *  Wraps, never truncates: no `truncate`, `nowrap` or fixed height, and
 *  `wrap-anywhere` for a name with no space to break at: `overflow-wrap:
 *  break-word` leaves the min-content width at the whole unbroken word, so a
 *  flex item would still overflow its column (measured in the browser at
 *  390px with a 60-character word); `anywhere` lets it shrink and break. The
 *  corner radius stays at 20px, so one line is a capsule and two are a
 *  rounded box, never an ellipse. */
const ACCEPT =
  "inline-flex min-h-9 max-w-full items-center rounded-[20px] border border-(--accent-amber-glow) px-3.5 py-1.5 text-left text-[13px] leading-snug wrap-anywhere text-(--accent-amber) transition-[color,border-color,transform,opacity] duration-150 ease-out hover:border-(--accent-amber-emphasis) hover:text-(--accent-amber-light) active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent-amber-emphasis) disabled:opacity-60 disabled:delay-150";

/** One answer of the ask: cream, on the recessed well, with the amber focus
 *  ring the quiet controls share. */
const ANSWER =
  "inline-flex min-h-9 items-center rounded-full border border-(--text-cream-border) px-3.5 py-1.5 text-[13px] text-(--text-cream) transition-[color,border-color,transform,opacity] duration-150 ease-out hover:border-(--text-cream-dim) active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent-amber-emphasis) disabled:opacity-60 disabled:delay-150";

const BODY = { fontFamily: "var(--font-body)" } as const;

export function OfferControls({
  offer,
  onAnswer,
  onDismiss,
}: {
  offer: OpenerOffer | null | undefined;
  onAnswer: (answer: string) => Promise<unknown>;
  onDismiss: () => Promise<unknown> | void;
}) {
  const view = offerView(offer);
  const { box, busy, failed, run } = useOpenerRun(view.kind);

  const failure = failed && <OpenerWriteFailed />;

  const gotIt = showsOfferGotIt(view) && (
    <button
      type="button"
      disabled={busy}
      onClick={() => run(onDismiss)}
      className={QUIET}
      style={BODY}
    >
      {COACH_COPY.openerGotIt}
    </button>
  );

  if (view.kind === "offer") {
    return (
      <div ref={box} className="flex flex-col items-start gap-1.5">
        <div className="flex max-w-full flex-wrap items-center gap-x-3 gap-y-1.5">
          <button
            type="button"
            data-emphasis="primary"
            disabled={busy}
            onClick={() => run(() => onAnswer("accepted"))}
            className={ACCEPT}
            // On the builder's recessed well, as the verdict's pill (PC-I53):
            // amber measures 6.23:1 there, over the gradient's mauve-tan band.
            style={{ background: RECESSED.panel, ...BODY }}
          >
            {view.accept}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => onAnswer("declined"))}
            className={QUIET}
            style={BODY}
          >
            {view.decline}
          </button>
        </div>
        <p className="m-0 text-[12px]" style={{ color: TOKENS.creamDim }}>
          {view.scope}
        </p>
        {failure}
      </div>
    );
  }

  if (view.kind === "ask") {
    return (
      <div ref={box} className="flex flex-col items-start gap-1.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {view.controls.map((c) => (
            <button
              key={c.answer}
              type="button"
              data-emphasis="equal"
              disabled={busy}
              onClick={() => run(() => onAnswer(c.answer))}
              className={ANSWER}
              // The builder's recessed well, as the verdict's pill sits on:
              // over the gradient's mauve-tan band a bare cream border and
              // text would lose their contrast (PC-I53).
              style={{ background: RECESSED.panel, ...BODY }}
            >
              {c.label}
            </button>
          ))}
        </div>
        {failure}
      </div>
    );
  }

  if (view.kind === "answered") {
    // The record line above its controls, the offer's own shape (PC-I57).
    return (
      <div ref={box} className="flex flex-col items-start gap-1">
        <p role="status" className="m-0 text-[13px] wrap-anywhere" style={{ color: TOKENS.creamSoft }}>
          {view.record}
        </p>
        {(view.undo || gotIt) && (
          <div className="flex flex-wrap items-center gap-x-1">
            {view.undo && (
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => onAnswer(OFFER_WITHDRAWN))}
                className={QUIET}
                style={BODY}
              >
                {COACH_COPY.proposalUndo}
              </button>
            )}
            {gotIt}
          </div>
        )}
        {failure}
      </div>
    );
  }

  return (
    <div ref={box} className="flex flex-col items-start gap-1">
      <div className="flex items-center">{gotIt}</div>
      {failure}
    </div>
  );
}
